import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { isSignedIn, checkLoginStatus } from "./useAuth";
import { useUserStore } from "../store/userStore";

let socket: WebSocket | undefined
let retryTimer: ReturnType<typeof setTimeout> | undefined
let refreshTimer: ReturnType<typeof setTimeout> | undefined
let pollTimer: ReturnType<typeof setInterval> | undefined
let backoff = 1000
let started = false

export function startUnreadNotificationPush(): void {
  if (started) return
  started = true
  const userStore = useUserStore()
  const route = useRoute()
  const identity = computed(() => isSignedIn.value ? userStore.email : '')

  function refresh() {
    if (isSignedIn.value && document.visibilityState === 'visible') userStore.fetchUnreadNotifications()
  }

  function refreshWithin(maxDelay: number) {
    clearTimeout(refreshTimer)
    refreshTimer = setTimeout(refresh, Math.random() * maxDelay)
  }

  function connect() {
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws'
    const current = new WebSocket(`${protocol}://${location.host}/api/v1/chat`)
    const isReconnect = pollTimer !== undefined
    let openedAt = 0
    socket = current
    current.addEventListener('open', () => {
      openedAt = Date.now()
      current.send(JSON.stringify({ command: 'SubscribeNotifications' }))
      clearInterval(pollTimer)
      pollTimer = undefined
      if (isReconnect) refreshWithin(5_000)
    })
    current.addEventListener('message', (event) => {
      const data = JSON.parse(event.data)
      if (data.command === 'UnreadNotificationsChanged') refreshWithin(data.response.user_ids === null ? 10_000 : 0)
    })
    current.addEventListener('close', (event) => {
      if (socket !== current) return
      socket = undefined
      pollTimer ??= setInterval(refresh, 30_000)
      if (event.code === 4401) {
        checkLoginStatus()
        backoff = 30_000
      } else if (openedAt && Date.now() - openedAt > 30_000) {
        backoff = 1000
      }
      retryTimer = setTimeout(connect, backoff * (0.5 + Math.random() / 2))
      backoff = Math.min(backoff * 2, 30_000)
    })
  }

  function disconnect() {
    const current = socket
    socket = undefined
    current?.close()
    clearTimeout(retryTimer)
    clearTimeout(refreshTimer)
    clearInterval(pollTimer)
    pollTimer = undefined
    backoff = 1000
  }

  watch(identity, (current, previous) => {
    if (previous) disconnect()
    if (current) connect()
  }, { immediate: true })

  watch([identity, () => route.path], ([current]) => {
    if (current) userStore.fetchUnreadNotifications()
  }, { immediate: true })

  document.addEventListener('visibilitychange', refresh)
}
